export interface TextBetweenDoc {
  textBetween: (from: number, to: number) => string;
}

/**
 * Shrink a selection range to exclude leading/trailing whitespace, so
 * selecting ` some link ` edits the existing link instead of offering a new
 * one (and new links don't swallow the surrounding spaces). Returns the
 * original range when it contains only whitespace.
 */
export const trimRangeWhitespace = (doc: TextBetweenDoc, range: { from: number; to: number }): { from: number; to: number } => {
  let { from, to } = range;
  while (from < to && /\s/.test(doc.textBetween(from, from + 1))) from += 1;
  while (to > from && /\s/.test(doc.textBetween(to - 1, to))) to -= 1;
  return from === to ? range : { from, to };
};

const HTTP_PROTOCOL_PATTERN = /^https?:\/\//i;
const MAILTO_PATTERN = /^mailto:/i;
// Any scheme-looking prefix (javascript:, data:, ftp:, ...) that we don't allow
const OTHER_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Normalize user-entered link text into a safe href.
 * Returns null when the input can't become a valid link: empty, contains
 * whitespace, or uses a scheme other than http(s)/mailto (e.g. javascript:).
 * Bare domains get https:// prepended ("google.com" -> "https://google.com").
 */
export const normalizeLinkHref = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return null;
  }

  if (MAILTO_PATTERN.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      return url.pathname ? trimmed : null;
    } catch {
      return null;
    }
  }

  if (!HTTP_PROTOCOL_PATTERN.test(trimmed) && OTHER_SCHEME_PATTERN.test(trimmed)) {
    return null;
  }

  const candidate = HTTP_PROTOCOL_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(candidate).hostname ? candidate : null;
  } catch {
    return null;
  }
};
